import { Box, FormField, InputArea, Loader, Popover, Text } from '@wix/design-system';
import { StatusWarningFilled } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import React, { useContext } from 'react';

import { RicosContext } from './../../ricos-context';

import styles from './prompt-input-area.rtlignore-scss';

type PromptInputAreaProps = {
  busy?: boolean;
  readonly?: boolean;
  placeholder?: string;
  maxLength: number;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  value: string;
  busyValue?: string;
  onCancel: () => void;
  zIndex: number;
  disabled?: boolean;
  dataHook: string;
  minRowsAutoGrow?: number;
  maxHeight?: string;
};

export type PromptInputAreaRef = {
  focus: () => void;
};

export const PromptInputArea = React.forwardRef<PromptInputAreaRef, PromptInputAreaProps>(
  (
    {
      busy,
      readonly,
      placeholder,
      maxLength,
      onChange,
      onSubmit,
      value,
      busyValue,
      onCancel,
      zIndex,
      disabled,
      dataHook,
      minRowsAutoGrow = 1,
      maxHeight = '66px',
    },
    ref
  ) => {
    const { t } = useContext(RicosContext);
    const inputRef = React.useRef<InputArea>(null);

    React.useImperativeHandle(
      ref,
      () => {
        return {
          focus() {
            inputRef.current?.focus();
          },
        };
      },
      []
    );

    React.useEffect(() => {
      inputRef.current?.calculateComputedRows();
    }, [busy, value]);

    return (
      <Box className={styles.inputWrapper} flexGrow={1}>
        <FormField>
          <InputArea
            disabled={disabled}
            dataHook={dataHook}
            size="small"
            autoGrow
            minRowsAutoGrow={minRowsAutoGrow}
            maxHeight={maxHeight}
            ref={inputRef}
            readOnly={busy || readonly}
            value={busy ? busyValue ?? t('AIPlugin_Input_Loading_Text') : value}
            placeholder={t(placeholder ?? 'AIPlugin_Input_Placeholder_Text')}
            onChange={e =>
              e.target.value.length > maxLength ? onChange(value) : onChange(e.target.value)
            }
            onKeyDown={e => {
              if (e.key === 'Escape') {
                if (busy) {
                  e.stopPropagation();
                  onCancel();
                  return;
                }

                if (value) {
                  e.stopPropagation();
                  onChange('');
                  inputRef.current?.calculateComputedRows();
                }
              }

              if (e.key === 'Enter' && !busy) {
                onSubmit(value);
                return;
              }
            }}
          />
        </FormField>
        <StatusIndicator
          busy={busy}
          maxLength={maxLength}
          valueLength={value.length}
          zIndex={zIndex}
        />
      </Box>
    );
  }
);

type StatusIndicatorProps = {
  busy?: boolean;
  maxLength: number;
  valueLength: number;
  zIndex: number;
};

const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  busy,
  maxLength,
  valueLength,
  zIndex,
}) => {
  const { t } = useContext(RicosContext);
  const [showPopover, setShowPopover] = React.useState(false);

  React.useEffect(() => {
    setShowPopover(maxLength === valueLength);
  }, [maxLength, valueLength]);

  if (busy) {
    return (
      <div className={classNames(styles.statusIndicator, styles.statusIndicatorLoader)}>
        <Loader size="tiny" />
      </div>
    );
  }

  if (valueLength === 0) {
    return null;
  }

  const indicator =
    maxLength === valueLength ? (
      <Box color="Y10" className={styles.statusIndicatorWarning}>
        <StatusWarningFilled />
      </Box>
    ) : null;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={styles.statusIndicator}
      onMouseEnter={() => setShowPopover(true)}
      onMouseLeave={() => setShowPopover(false)}
    >
      <Popover
        moveBy={{ y: 6 }}
        shown={showPopover}
        appendTo="window"
        placement="top"
        theme="dark"
        zIndex={zIndex + 1}
      >
        <Popover.Element>{indicator}</Popover.Element>
        <Popover.Content>
          <Box
            paddingTop="SP1"
            paddingBottom="SP1"
            paddingLeft="SP2"
            paddingRight="SP2"
            direction="vertical"
            maxWidth="200px"
          >
            <Text size="tiny" light weight="thin">
              {t(
                maxLength === valueLength
                  ? 'AIPlugin_Characters_Completed_Tooltip_Text'
                  : 'AIPlugin_Characters_Tooltip_Text',
                { count: valueLength, limit: maxLength }
              )}
            </Text>
          </Box>
        </Popover.Content>
      </Popover>
    </div>
  );
};
